import { NodeSelection } from '../../../selection/selection';
import { getBlockContentElement, normalizeUrl } from '../../../common/utils/index';
import { events } from '../../../common/constant';
import * as constants from '../../../common/constant';
/**
 * `LinkModule` module is used to handle hyperlinks in the block editor
 *
 * @hidden
 */
export class LinkModule {
    constructor(manager) {
        this.parent = manager;
        this.selectionManager = new NodeSelection(this.parent.blockContainer);
        this.addEventListeners();
    }
    addEventListeners() {
        this.parent.observer.on('linkPopupCreated', this.handleLinkCreated, this);
        this.parent.observer.on(events.editorClick, this.handleEditorClick, this);
        this.parent.observer.on(events.documentClick, this.handleDocumentClick, this);
        this.parent.observer.on(events.formattingPerformed, this.formattingPerformed, this);
        this.parent.observer.on('linkPopupAfterOpen', this.handleLinkPopupAfterOpen, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.parent.observer.off(events.editorClick, this.handleEditorClick);
        this.parent.observer.off(events.documentClick, this.handleDocumentClick);
        this.parent.observer.off(events.formattingPerformed, this.formattingPerformed);
        this.parent.observer.off('linkPopupAfterOpen', this.handleLinkPopupAfterOpen);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    handleLinkCreated() {
        this.popupElement = document.querySelector('#' + this.parent.rootEditorElement.id + constants.LINKDIALOG_ID);
        if (this.popupElement) {
            this.bindPopupEvents();
        }
    }
    bindPopupEvents() {
        const insertButton = this.popupElement.querySelector('.e-insert-link-btn');
        const removeButton = this.popupElement.querySelector('.e-remove-link-btn');
        const cancelButton = this.popupElement.querySelector('.e-cancel-link-btn');
        insertButton.addEventListener('click', this.handleLinkInsertDeletion.bind(this));
        removeButton.addEventListener('click', this.handleLinkInsertDeletion.bind(this, true));
        cancelButton.addEventListener('click', () => this.hideLinkPopup());
        const linkDialog = this.parent.rootEditorElement.querySelector('#' + this.parent.rootEditorElement.id + constants.LINKDIALOG_ID);
        const urlInput = linkDialog && linkDialog.querySelector('#linkUrl');
        if (urlInput) {
            urlInput.addEventListener('input', () => this.syncButtonsByUrlField());
            // Allow Enter to submit when URL has content, but do NOT steal focus while typing
            urlInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const hasText = (urlInput.value || '').trim().length > 0;
                    if (hasText) {
                        this.handleLinkInsertDeletion(e);
                    }
                }
            });
        }
        this.popupElement.addEventListener('keydown', (e) => {
            const target = e.target;
            if (e.key === 'Enter') {
                e.preventDefault();
                if (target.classList.contains('e-insert-link-btn')) {
                    this.handleLinkInsertDeletion(e);
                }
                else if (target.classList.contains('e-remove-link-btn')) {
                    this.handleLinkInsertDeletion(e, true);
                }
                else if (target.classList.contains('e-cancel-link-btn')) {
                    this.hideLinkPopup();
                }
            }
            else if (e.key === 'Escape') {
                e.preventDefault();
                this.hideLinkPopup();
            }
        });
    }
    formattingPerformed(e) {
        if (e.subCommand === 'link') {
            this.hideLinkPopup();
        }
    }
    handleDocumentClick(e) {
        const target = e.target;
        if (!this.popupElement) {
            return;
        }
        if (this.popupElement.classList.contains('e-popup-open')
            && (!this.popupElement.contains(target))) {
            this.hideLinkPopup();
        }
    }
    handleEditorClick(e) {
        const target = e.target;
        if (target.tagName === 'A' || target.closest('a')) {
            e.preventDefault();
            const linkElement = target.tagName === 'A'
                ? target
                : target.closest('a');
            this.handleLinkClick(linkElement);
        }
    }
    /**
     * Shows the link popup dialog at cursor position
     *
     * @param {KeyboardEvent} e - Keyboard event that triggered the popup
     * @returns {void}
     * @hidden
     */
    showLinkPopup(e) {
        this.parent.inlineToolbarModule.hideInlineToolbar(e);
        const contentElement = getBlockContentElement(this.parent.currentFocusedBlock);
        const selectedBlocks = this.parent.editorMethods.getSelectedBlocks();
        if (!contentElement || (selectedBlocks && selectedBlocks.length > 1)) {
            return;
        }
        this.selectionManager.saveSelection();
        const positionY = this.getDialogPosition();
        this.parent.observer.notify('showLinkPopup', { x: 'center', y: positionY });
    }
    getDialogPosition() {
        let distanceFromVisibleTop = this.parent.rootEditorElement.getBoundingClientRect().top;
        if (distanceFromVisibleTop < 0) {
            distanceFromVisibleTop = Math.abs(distanceFromVisibleTop) + this.parent.rootEditorElement.scrollTop;
            return distanceFromVisibleTop.toString();
        }
        else {
            return this.parent.rootEditorElement.scrollTop.toString();
        }
    }
    handleLinkPopupAfterOpen() {
        setTimeout(() => {
            this.populateInputFields();
        }, 10);
    }
    populateInputFields() {
        const linkDialog = this.parent.rootEditorElement.querySelector('#' + this.parent.rootEditorElement.id + constants.LINKDIALOG_ID);
        const urlInput = linkDialog.querySelector('#linkUrl');
        const textInput = linkDialog.querySelector('#linkText');
        const titleInput = linkDialog.querySelector('#linkTitle');
        if (this.selectionManager && !this.selectionManager.isCollapsed()) {
            textInput.value = this.selectionManager.getSelectedText();
        }
        const linkInfo = this.getLinkFromSelection();
        if (linkInfo) {
            textInput.value = linkInfo.text;
            urlInput.value = linkInfo.url;
            titleInput.value = linkInfo.title;
        }
        // Ensure Remove button reflects whether a link existed in selection
        const removeBtn = this.popupElement.querySelector('.e-remove-link-btn');
        if (linkInfo) {
            removeBtn.removeAttribute('disabled');
        }
        else {
            removeBtn.setAttribute('disabled', '');
        }
        // Sync Insert button based on URL input
        this.syncButtonsByUrlField();
    }
    syncButtonsByUrlField() {
        const linkDialog = this.parent.rootEditorElement.querySelector('#' + this.parent.rootEditorElement.id + constants.LINKDIALOG_ID);
        const urlInput = linkDialog.querySelector('#linkUrl');
        const insertBtn = this.popupElement.querySelector('.e-insert-link-btn');
        const hasText = urlInput.value.trim().length > 0;
        if (hasText) {
            insertBtn.removeAttribute('disabled');
        }
        else {
            insertBtn.setAttribute('disabled', '');
        }
    }
    /**
     * Hides the link popup dialog and restores selection
     *
     * @returns {void}
     * @hidden
     */
    hideLinkPopup() {
        if (this.popupElement && this.popupElement.classList.contains('e-popup-close')) {
            return;
        }
        this.clearInputValues();
        this.parent.observer.notify('hideLinkPopup');
        const contentElement = getBlockContentElement(this.parent.currentFocusedBlock);
        if (contentElement) {
            this.selectionManager.restoreSelection();
        }
    }
    clearInputValues() {
        const linkDialog = this.parent.rootEditorElement.querySelector('#' + this.parent.rootEditorElement.id + constants.LINKDIALOG_ID);
        const textInput = linkDialog.querySelector('#linkText');
        const urlInput = linkDialog.querySelector('#linkUrl');
        const titleInput = linkDialog.querySelector('#linkTitle');
        textInput.value = '';
        urlInput.value = '';
        titleInput.value = '';
        // Disable Remove button when inputs are cleared (Remove should not follow URL input)
        const removeBtn = this.popupElement.querySelector('.e-remove-link-btn');
        removeBtn.setAttribute('disabled', '');
        this.syncButtonsByUrlField();
    }
    handleLinkInsertDeletion(e, isRemove) {
        const contentElement = getBlockContentElement(this.parent.currentFocusedBlock);
        const linkDialog = this.parent.rootEditorElement.querySelector('#' + this.parent.rootEditorElement.id + constants.LINKDIALOG_ID);
        if (!contentElement) {
            return;
        }
        this.selectionManager.restoreSelection();
        if (isRemove) {
            this.parent.formattingAction.execCommand({ subCommand: 'link', value: null });
            return;
        }
        const textInput = linkDialog.querySelector('#linkText');
        const urlInput = linkDialog.querySelector('#linkUrl');
        const titleInput = linkDialog.querySelector('#linkTitle');
        const url = normalizeUrl(urlInput.value);
        if (!url) {
            urlInput.focus();
            return;
        }
        this.parent.formattingAction.execCommand({
            subCommand: 'link',
            value: { text: textInput.value, url, title: titleInput.value }
        });
    }
    handleLinkClick(link) {
        const url = link.getAttribute('href');
        if (url) {
            window.open(url, link.getAttribute('target') || '_self');
        }
    }
    getLinkFromSelection() {
        if (!this.selectionManager) {
            return null;
        }
        const linkElement = this.selectionManager.getNodeFromSelection('a');
        if (linkElement) {
            const link = linkElement;
            return {
                text: link.textContent,
                url: link.getAttribute('href'),
                title: link.getAttribute('title')
            };
        }
        return null;
    }
    /**
     * Checks whether the slash command popup is opened or not.
     *
     * @returns {boolean} - Returns true if the slash command popup is opened, otherwise false.
     * @hidden
     */
    isPopupOpen() {
        const linkDialogId = `${this.parent.rootEditorElement.id + constants.LINKDIALOG_ID}`;
        const linkPopup = document.querySelector(`#${linkDialogId}.${constants.LINKDIALOG_CLS}`);
        return (linkPopup && linkPopup.classList.contains('e-popup-open'));
    }
    /**
     * Destroys the link module and cleans up resources
     *
     * @returns {void}
     */
    destroy() {
        this.removeEventListeners();
        this.selectionManager = null;
        this.popupElement = null;
    }
}
